import { atom } from 'nanostores';

import { api, type ApiKey } from '../lib/api';
import { isStaleSession, messageOf, onSessionCleared, refreshMe, sessionEpoch } from './auth';

import { track } from '../analytics/track';

export const $keys = atom<ApiKey[]>([]);
export const $maxKeys = atom(0);
export const $keysLoading = atom(false);
export const $keysError = atom<string | null>(null);

/**
 * The plaintext of a freshly created key. Held in memory only — it is shown
 * once and never persisted anywhere on the client.
 */
export const $revealedKey = atom<ApiKey | null>(null);

export function resetKeys(): void {
  $keys.set([]);
  $maxKeys.set(0);
  $keysLoading.set(false);
  $keysError.set(null);
  $revealedKey.set(null);
}

onSessionCleared(resetKeys);

export async function loadKeys(): Promise<void> {
  const captured = sessionEpoch();
  $keysLoading.set(true);
  $keysError.set(null);
  try {
    const res = await api.listKeys();
    if (isStaleSession(captured)) return;
    $keys.set(res.keys);
    $maxKeys.set(res.max_keys);
  } catch (err) {
    $keysError.set(messageOf(err));
  } finally {
    $keysLoading.set(false);
  }
}

export async function createKey(name: string, test: boolean): Promise<ApiKey> {
  $keysError.set(null);
  try {
    const res = await api.createKey({ name, test });
    $revealedKey.set(res.key);
    // Which key this is matters more than that one exists: the first is
    // activation, a later one usually means a second environment.
    track('key_created', { index: $keys.get().length + 1, test });
    await loadKeys();
    await refreshMe();
    return res.key;
  } catch (err) {
    $keysError.set(messageOf(err));
    throw err;
  }
}

export async function revokeKey(id: string): Promise<void> {
  $keysError.set(null);
  try {
    await api.revokeKey(id);
    // A revoked key's plaintext must stop being offered for copy.
    if ($revealedKey.get()?.id === id) $revealedKey.set(null);
    await loadKeys();
    await refreshMe();
  } catch (err) {
    $keysError.set(messageOf(err));
    throw err;
  }
}

export function dismissRevealedKey(): void {
  $revealedKey.set(null);
}
